/**
 * Gym hooks — TanStack Query hooks for gym endpoints.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  Biometrics,
  BiometricsCreate,
  Macros,
  MacrosCreate,
  Exercise,
  ExerciseCreate,
  Routine,
  RoutineCreate,
  RoutineUpdate,
  GymSession,
  SessionCreate,
  SessionUpdate,
  GymSet,
  SetCreate,
  SetUpdate,
  SummaryStats,
  VolumeByGroup,
  ExerciseProgression,
  GymImportResult,
  GymInsights,
  SetComplete,
} from "@black-wall/shared";

const BASE = "/api/gym";

// ============================================================================
// Biometrics
// ============================================================================

export function useBiometrics(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const query = params.toString();
  
  return useQuery<Biometrics[]>({
    queryKey: ["gym", "biometrics", from, to],
    queryFn: async () => {
      const res = await fetch(`${BASE}/biometrics${query ? `?${query}` : ""}`);
      return res.json();
    },
  });
}

export function useUpsertBiometrics() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: BiometricsCreate) => {
      const res = await fetch(`${BASE}/biometrics`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "biometrics"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

export function useDeleteBiometrics() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/biometrics/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "biometrics"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

// ============================================================================
// Macros
// ============================================================================

export function useMacros(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const query = params.toString();
  
  return useQuery<Macros[]>({
    queryKey: ["gym", "macros", from, to],
    queryFn: async () => {
      const res = await fetch(`${BASE}/macros${query ? `?${query}` : ""}`);
      return res.json();
    },
  });
}

export function useUpsertMacros() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: MacrosCreate) => {
      const res = await fetch(`${BASE}/macros`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "macros"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

export function useDeleteMacros() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/macros/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "macros"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

// ============================================================================
// Exercises
// ============================================================================

export function useExercises(muscleGroup?: string, q?: string) {
  const params = new URLSearchParams();
  if (muscleGroup) params.set("muscleGroup", muscleGroup);
  if (q) params.set("q", q);
  const query = params.toString();
  
  return useQuery<Exercise[]>({
    queryKey: ["gym", "exercises", muscleGroup, q],
    queryFn: async () => {
      const res = await fetch(`${BASE}/exercises${query ? `?${query}` : ""}`);
      return res.json();
    },
  });
}

export function useCreateExercise() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: ExerciseCreate) => {
      const res = await fetch(`${BASE}/exercises`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "exercises"] });
    },
  });
}

export function useDeleteExercise() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/exercises/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "exercises"] });
    },
  });
}

// ============================================================================
// Routines
// ============================================================================

export function useRoutines() {
  return useQuery<(Routine & { exercise_count: number })[]>({
    queryKey: ["gym", "routines"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/routines`);
      return res.json();
    },
  });
}

export function useRoutine(id: number | null) {
  return useQuery<Routine>({
    queryKey: ["gym", "routines", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/routines/${id}`);
      return res.json();
    },
    enabled: id !== null,
  });
}

export function useCreateRoutine() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: RoutineCreate) => {
      const res = await fetch(`${BASE}/routines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "routines"] });
    },
  });
}

export function useUpdateRoutine() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: RoutineUpdate }) => {
      const res = await fetch(`${BASE}/routines/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "routines"] });
    },
  });
}

export function useDeleteRoutine() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/routines/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "routines"] });
    },
  });
}

// ============================================================================
// Sessions
// ============================================================================

export function useSessions(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const query = params.toString();
  
  return useQuery<GymSession[]>({
    queryKey: ["gym", "sessions", from, to],
    queryFn: async () => {
      const res = await fetch(`${BASE}/sessions${query ? `?${query}` : ""}`);
      return res.json();
    },
  });
}

export function useSession(id: number | null) {
  return useQuery<GymSession>({
    queryKey: ["gym", "sessions", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/sessions/${id}`);
      return res.json();
    },
    enabled: id !== null,
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: SessionCreate) => {
      const res = await fetch(`${BASE}/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "sessions"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SessionUpdate }) => {
      const res = await fetch(`${BASE}/sessions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["gym", "sessions"] });
      qc.invalidateQueries({ queryKey: ["gym", "sessions", id] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/sessions/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "sessions"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

// ============================================================================
// Sets
// ============================================================================

export function useAddSet() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sessionId, data }: { sessionId: number; data: SetCreate }) => {
      const res = await fetch(`${BASE}/sessions/${sessionId}/sets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: (_, { sessionId }) => {
      qc.invalidateQueries({ queryKey: ["gym", "sessions", sessionId] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

export function useUpdateSet() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SetUpdate }) => {
      const res = await fetch(`${BASE}/sets/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "sessions"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

export function useDeleteSet() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/sets/${id}`, { method: "DELETE" });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "sessions"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}

// ============================================================================
// Stats
// ============================================================================

export function useSummaryStats() {
  return useQuery<SummaryStats>({
    queryKey: ["gym", "stats", "summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/summary`);
      return res.json();
    },
    refetchInterval: 60_000, // Refetch every minute
  });
}

export function useVolumeByGroup(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const query = params.toString();
  
  return useQuery<VolumeByGroup[]>({
    queryKey: ["gym", "stats", "volume", from, to],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/volume${query ? `?${query}` : ""}`);
      return res.json();
    },
  });
}

export function useExerciseProgression(exerciseId: number | null) {
  return useQuery<ExerciseProgression[]>({
    queryKey: ["gym", "stats", "exercise", exerciseId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/exercise/${exerciseId}`);
      return res.json();
    },
    enabled: exerciseId !== null,
  });
}

export function useInsights() {
  return useQuery<GymInsights>({
    queryKey: ["gym", "stats", "insights"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/insights`);
      return res.json();
    },
  });
}

export function useGymImport() {
  const qc = useQueryClient();
  return useMutation<GymImportResult, Error, string>({
    mutationFn: async (text: string) => {
      const res = await fetch(`${BASE}/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym"] });
    },
  });
}

export function useGymImportText() {
  const qc = useQueryClient();
  return useMutation<GymImportResult, Error, string>({
    mutationFn: async (text: string) => {
      const res = await fetch(`${BASE}/import/text`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym"] });
    },
  });
}

// ============================================================================
// Analytics
// ============================================================================

export interface HeatmapEntry {
  date: string;
  sessions: number;
  volume: number;
  exercises: number;
}

export function useHeatmap(from?: string) {
  const params = from ? `?from=${from}` : "";
  return useQuery<{ heatmap: HeatmapEntry[] }>({
    queryKey: ["gym", "stats", "heatmap", from],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/heatmap${params}`);
      return res.json();
    },
  });
}

export interface E1rmExercise {
  exercise_id: number;
  exercise_name: string;
  muscle_group: string;
  current_e1rm: number;
  peak_e1rm: number;
  progression: { date: string; e1rm: number }[];
}

export function useEstimated1RM() {
  return useQuery<{ exercises: E1rmExercise[] }>({
    queryKey: ["gym", "stats", "e1rm"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/estimated-1rm`);
      return res.json();
    },
  });
}

export interface WeeklyVolumeEntry {
  week: string;
  volume: number;
  sets: number;
  sessions: number;
}

export function useWeeklyVolume(weeks = 12) {
  return useQuery<{ weeks: WeeklyVolumeEntry[] }>({
    queryKey: ["gym", "stats", "weekly-volume", weeks],
    queryFn: async () => {
      const res = await fetch(`${BASE}/stats/weekly-volume?weeks=${weeks}`);
      return res.json();
    },
  });
}

// ============================================================================
// Planned sessions (mobile PWA phase 2)
// ============================================================================

export function usePlanToday(date?: string) {
  return useQuery<GymSession | { error: string }>({
    queryKey: ["gym", "plan", date ?? "today"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/plan/today${date ? `?date=${date}` : ""}`);
      return res.json();
    },
    refetchInterval: 60_000,
  });
}

export function useCompleteSet() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ setId, data }: { setId: number; data: SetComplete }) => {
      const res = await fetch(`${BASE}/sets/${setId}/complete`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gym", "plan"] });
      qc.invalidateQueries({ queryKey: ["gym", "sessions"] });
      qc.invalidateQueries({ queryKey: ["gym", "stats"] });
    },
  });
}
